import React, { useState, useRef, useEffect } from 'react'
import styled from 'styled-components'
import { SignOut, User, LockKey } from 'phosphor-react'
import { useAuth } from 'context/user'
import { useNavigate } from 'react-router-dom'
import { AuthService } from 'utility'
import { UserEditModal } from 'page-components/settings/tabs/user/UserEditModal'
import { ChangePasswordModal } from './ChangePasswordModal'
import { useImpersonation } from 'context/impersonation'
import { useSubscriptionUsage } from 'context/subscription-usage'

const ProfileContainer = styled.div`
    position: relative;
    display: inline-block;
`

const ProfileIcon = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #263BA6;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: white;
    font-weight: bold;
    font-size: 16px;
    transition: all 0.2s ease;
    overflow: hidden; /* Ensure image is clipped to the circle */

    &:hover {
        transform: scale(1.05);
        box-shadow: 0 2px 8px rgba(0,0,0,0.2);
    }
`

const DropdownMenu = styled.div`
    position: absolute;
    top: 100%;
    right: 0;
    background: white;
    border: 1px solid #e8e9e9;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    min-width: 280px;
    z-index: 1000;
    margin-top: 8px;

    &::before {
        content: '';
        position: absolute;
        top: -8px;
        right: 16px;
        width: 0;
        height: 0;
        border-left: 8px solid transparent;
        border-right: 8px solid transparent;
        border-bottom: 8px solid white;
    }

    &::after {
        content: '';
        position: absolute;
        top: -9px;
        right: 16px;
        width: 0;
        height: 0;
        border-left: 8px solid transparent;
        border-right: 8px solid transparent;
        border-bottom: 8px solid #e8e9e9;
    }
`

const UserInfo = styled.div`
    padding: 16px;
    border-bottom: 1px solid #f0f0f0;
`

const UserName = styled.div`
    font-size: 18px;
    font-weight: 600;
    color: #333;
    margin-bottom: 4px;
`

const UserRole = styled.div`
    font-size: 14px;
    color: #666;
    margin-bottom: 4px;
`

const ServicePlan = styled.div`
    font-size: 14px;
    color: #666;
    margin-bottom: 4px;
`

const CompanyName = styled.div`
    font-size: 14px;
    color: #666;
`

const MenuItem = styled.div`
    padding: 12px 16px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 12px;
    transition: background-color 0.2s ease;

    &:hover {
        background-color: #f8f9fa;
    }

    &:last-child {
        border-top: 1px solid #f0f0f0;
        color: #dc3545;
    }
`

const MenuItemText = styled.span`
    font-size: 14px;
`

export const ProfileDropdown = () => {
    const { user, logout, updateUser } = useAuth()
    const { isImpersonating } = useImpersonation()
    const { planLabel, usage, isFreePlan } = useSubscriptionUsage()
    const navigate = useNavigate()
    const [isOpen, setIsOpen] = useState(false)
    const [showEditModal, setShowEditModal] = useState(false)
    const [showChangePasswordModal, setShowChangePasswordModal] = useState(false)
    const dropdownRef = useRef(null)
    const [imageError, setImageError] = useState(false)

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    const handleLogout = async () => {
        await AuthService.logout()
        logout()
        const dashboardId = user?.DashboardStyle?.DashboardId
        navigate(dashboardId === 2 ? '/sign-in-franchising' : '/sign-in')
        setIsOpen(false)
    }

    const handleEditProfile = () => {
        setIsOpen(false)
        setShowEditModal(true)
    }

    const handleChangePassword = () => {
        setIsOpen(false)
        setShowChangePasswordModal(true)
    }

    const handleEditModalClose = () => {
        setShowEditModal(false)
    }

    const handleChangePasswordModalClose = () => {
        setShowChangePasswordModal(false)
    }

    const handleUserUpdate = (updatedUser) => {
        // Update user in localStorage and context
        if (updateUser) {
            updateUser(updatedUser)
        } else {
            AuthService.storeUser(updatedUser)
            // Fallback: reload if updateUser is not available
            window.location.reload()
        }
    }

    const toggleDropdown = () => {
        setIsOpen(!isOpen)
    }

    const getInitials = (name) => {
        if (!name) return 'U'
        return name.split(' ').map(n => n[0]).join('').toUpperCase()
            .slice(0, 2)
    }

    const getServicePlanText = (servicePlan) => {
        if (!servicePlan || servicePlan === 0) return 'Free'
        return `Plan ${servicePlan}`
    }

    const getCompanyName = () => {
        if (user?.Companies && user.Companies.length > 0) {
            return user.Companies[0].CompanyName
        }
        return user?.CompanyName || 'N/A'
    }

    const getServicePlan = () => {
        if (user?.Companies && user.Companies.length > 0) {
            return user.Companies[0].ServicePlan
        }
        return user?.ServicePlan
    }

    const servicePlanText = () => {
        if (planLabel) return planLabel
        if (!isFreePlan && usage) {
            const name = String(usage.planName || '').trim()
            const tier = String(usage.tier || '').trim()
            if (name && name !== 'Free') return name
            if (tier && tier !== 'Free' && tier !== 'Unknown') return tier
        }
        return getServicePlanText(getServicePlan())
    }

    if (!user) return null

    return (
        <ProfileContainer ref={dropdownRef}>
            <ProfileIcon onClick={toggleDropdown}>
                {user.Image && !imageError ? (
                    <img
                        src={user.Image}
                        alt="Profile"
                        onError={() => setImageError(true)}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                        referrerPolicy="no-referrer"
                    />
                ) : (
                    getInitials(user.FullName)
                )}
            </ProfileIcon>

            {isOpen && (
                <DropdownMenu>
                    <UserInfo>
                        <UserName>{user.FullName || 'N/A'}</UserName>
                        <UserRole>Role: {user.Role || 'N/A'}</UserRole>
                        <ServicePlan>Service Plan: {servicePlanText()}</ServicePlan>
                        <CompanyName>Company: {getCompanyName()}</CompanyName>
                    </UserInfo>

                    <MenuItem onClick={handleEditProfile}>
                        <User size={16} />
                        <MenuItemText>Edit Profile</MenuItemText>
                    </MenuItem>

                    {!isImpersonating && (
                        <MenuItem onClick={handleChangePassword}>
                            <LockKey size={16} />
                            <MenuItemText>Change Password</MenuItemText>
                        </MenuItem>
                    )}

                    <MenuItem onClick={handleLogout}>
                        <SignOut size={16} />
                        <MenuItemText>Log Out</MenuItemText>
                    </MenuItem>
                </DropdownMenu>
            )}

            <UserEditModal
                open={showEditModal}
                onClose={handleEditModalClose}
                user={user}
                isProfileEdit
                onUserUpdate={handleUserUpdate}
            />

            <ChangePasswordModal
                open={showChangePasswordModal}
                onClose={handleChangePasswordModalClose}
                user={user}
            />
        </ProfileContainer>
    )
}
